import { Injectable } from '@angular/core';
import { VerificationModalService } from '@app/pages/documents/shared/verification-modal/verification-modal.service';

import { SecurityVerificationEnum, SecurityVerificationType, UserBiometricValidation } from '@app/models';

import {
  CheckUserBiometricLivenessResultsGQL,
  CheckUserBiometricLivenessResultsMutationVariables,
  BiometricSignedUrlsGQL,
  BiometricSignedUrlsQueryVariables,
  ExecuteUserBiometricValidationGQL,
  ExecuteUserBiometricValidationMutationVariables,
  UserBiometricValidationGQL,
  UserBiometricValidationQueryVariables,
  CreateUserBiometricLivenessSessionGQL,
  CreateUserBiometricLivenessSessionMutationVariables,
  SignatureBySecurityVerificationCodeQueryVariables,
  PrepareBiometricAuthMutationVariables,
  SignatureBySecurityVerificationCodeGQL,
  PrepareBiometricAuthGQL
} from '../../generated/graphql.public';

import {
  BiometriqueOperationUrlsGQL,
  BiometriqueOperationUrlsQueryVariables,
  CheckLivenessResultsGQL,
  CheckLivenessResultsMutationVariables,
  CheckSecurityVerificationGQL,
  CheckSecurityVerificationMutationVariables,
  CreateBiometriqueOperationGQL,
  CreateBiometriqueOperationMutationVariables,
  CreateLivenessSessionGQL,
  CreateLivenessSessionMutationVariables,
  ExecuteBiometriqueVerificationGQL,
  ExecuteBiometriqueVerificationMutationVariables,
  GenerateSecurityVerificationCodeGQL,
  GenerateSecurityVerificationCodeMutationVariables,
  ResetBiometricAttemptsGQL,
  ResetBiometricAttemptsMutationVariables,
  SecurityVerificationGQL,
  SecurityVerificationQueryVariables,
  StartUserBiometricValidationGQL,
  UpdateSecurityVerificationGQL,
  UpdateSecurityVerificationMutationVariables
} from '../../generated/graphql.default';

import { throwOnGraphqlError } from '@app/core';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class VerificationService {
  constructor(
    private verificationModalService: VerificationModalService,
    private generateSecurityVerificationCodeGQL: GenerateSecurityVerificationCodeGQL,
    private startUserBiometricValidationGQL: StartUserBiometricValidationGQL,
    private checkUserBiometricLivenessResultsGQL: CheckUserBiometricLivenessResultsGQL,
    private biometricSignedUrlsGQL: BiometricSignedUrlsGQL,
    private executeUserBiometricValidationGQL: ExecuteUserBiometricValidationGQL,
    private userBiometricValidationGQL: UserBiometricValidationGQL,
    private createUserBiometricLivenessSessionGQL: CreateUserBiometricLivenessSessionGQL,
    private updateSecurityVerificationGQL: UpdateSecurityVerificationGQL,
    private checkSecurityVerificationGQL: CheckSecurityVerificationGQL,
    private signatureBySecurityVerificationCodeGQL: SignatureBySecurityVerificationCodeGQL,
    private createBiometriqueOperationGQL: CreateBiometriqueOperationGQL,
    private executeBiometriqueVerificationGQL: ExecuteBiometriqueVerificationGQL,
    private biometriqueOperationUrlsGQL: BiometriqueOperationUrlsGQL,
    private securityVerificationGQL: SecurityVerificationGQL,
    private createLivenessSessionGQL: CreateLivenessSessionGQL,
    private checkLivenessResultsGQL: CheckLivenessResultsGQL,
    private resetBiometricAttemptsGQL: ResetBiometricAttemptsGQL,
    private prepareBiometricAuthGQL: PrepareBiometricAuthGQL
  ) {}

  generateSecurityVerificationCode(variables: GenerateSecurityVerificationCodeMutationVariables) {
    return this.generateSecurityVerificationCodeGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.generateSecurityVerificationCode)
    );
  }

  signatureBySecurityVerificationCode(variables: SignatureBySecurityVerificationCodeQueryVariables) {
    return this.signatureBySecurityVerificationCodeGQL.fetch(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.signatureBySecurityVerificationCode)
    );
  }

  updateSecurityVerification(variables: UpdateSecurityVerificationMutationVariables) {
    return this.updateSecurityVerificationGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.updateSecurityVerification)
    );
  }

  checkSecurityVerification(variables: CheckSecurityVerificationMutationVariables) {
    return this.checkSecurityVerificationGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.checkSecurityVerification)
    );
  }

  createBiometriqueOperation(variables: CreateBiometriqueOperationMutationVariables) {
    return this.createBiometriqueOperationGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.createBiometriqueOperation)
    );
  }

  executeBiometriqueVerification(variables: ExecuteBiometriqueVerificationMutationVariables) {
    return this.executeBiometriqueVerificationGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.executeBiometriqueVerification)
    );
  }

  biometriqueOperationUrls(variables: BiometriqueOperationUrlsQueryVariables) {
    return this.biometriqueOperationUrlsGQL.fetch(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.biometriqueOperationUrls)
    );
  }

  createLivenessSession(variables: CreateLivenessSessionMutationVariables) {
    return this.createLivenessSessionGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.createLivenessSession)
    );
  }

  checkLivenessResults(variables: CheckLivenessResultsMutationVariables) {
    return this.checkLivenessResultsGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.checkLivenessResults)
    );
  }

  securityVerification(variables: SecurityVerificationQueryVariables) {
    return this.securityVerificationGQL.fetch(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.securityVerification)
    );
  }

  prepareBiometricAuth(variables: PrepareBiometricAuthMutationVariables) {
    return this.prepareBiometricAuthGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.prepareBiometricAuth)
    );
  }

  resetBiometricAttempts(variables: ResetBiometricAttemptsMutationVariables) {
    return this.resetBiometricAttemptsGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.resetBiometricAttempts)
    );
  }

  startUserBiometricValidation() {
    return this.startUserBiometricValidationGQL.mutate().pipe(
      throwOnGraphqlError(),
      map(response => response.data.startUserBiometricValidation)
    );
  }

  createUserBiometricLivenessSession(variables: CreateUserBiometricLivenessSessionMutationVariables) {
    return this.createUserBiometricLivenessSessionGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.createUserBiometricLivenessSession)
    );
  }

  checkUserBiometricLivenessResults(variables: CheckUserBiometricLivenessResultsMutationVariables) {
    return this.checkUserBiometricLivenessResultsGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.checkUserBiometricLivenessResults)
    );
  }

  userBiometricValidation(variables: UserBiometricValidationQueryVariables) {
    return this.userBiometricValidationGQL.fetch(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.userBiometricValidation)
    );
  }

  executeUserBiometricValidation(variables: ExecuteUserBiometricValidationMutationVariables) {
    return this.executeUserBiometricValidationGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.executeUserBiometricValidation)
    );
  }

  biometricSignedUrls(variables: BiometricSignedUrlsQueryVariables) {
    return this.biometricSignedUrlsGQL.fetch(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.biometricSignedUrls)
    );
  }

  validateUserCreateDocument(operationId?: string) {
    const verification: SecurityVerificationType = {
      type: SecurityVerificationEnum.LivenessAndTextExtraction,
      max_attempt: 3
    };

    return this.verificationModalService.open({ verification, operationId });
  }
}
